import { lazy, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loadable from "../components/Loadable";
import RouteGuard from "../components/RouteGuard/RouteGuard";
import { CUSTOMER } from "../config/constants";
import GeneralLayout from "../layouts/generalLayout/GeneralLayout";
import GeneralLayoutAlt from "../layouts/generalLayoutAlt/GeneralLayoutAlt";
import {
  IAuthState,
  resetLoggedOut,
  tryConfirmLogin,
} from "../stateManagement/reducers/authReducer";
import { IRootReducerT } from "../stateManagement/reduxStore";

interface IPageTemplateProps {
  altRoute: string;
  page: ReactNode;
  pageName: string;
}

const PageTemplate: React.FC<IPageTemplateProps> = ({
  pageName,
  altRoute,
  page,
}) => {
  const dispatch = useDispatch<any>();
  const { authToken, user, loggedOut } = useSelector<IRootReducerT, IAuthState>(
    (state) => state.auth,
  );
  let conditions: boolean[] = [];
  const hasValidAuthToken = authToken !== null;
  const isTime4Email2FA =
    hasValidAuthToken &&
    user?.usesEmail2FA === true &&
    user.email2FADone === false;
  const isTime4Authenticator2FA =
    hasValidAuthToken &&
    !isTime4Email2FA &&
    user?.usesAuthenticator2FA === true &&
    user.authenticator2FADone === false;
  const isCustomer =
    !user || (user && user?.role?.name === CUSTOMER) ? true : false;

  const isAuthenticated =
    hasValidAuthToken && !isTime4Email2FA && !isTime4Authenticator2FA;

  switch (pageName) {
    default:
      conditions = [isAuthenticated, isCustomer]; //User must be authenticated to access this route
      break;
  }

  const [isInitRender120, setIsInitRender120] = useState(true);
  useEffect(() => {
    if (isInitRender120) {
      setIsInitRender120(false);
      dispatch(resetLoggedOut({ authToken: authToken ?? "" }));
      return;
    }

    if (!authToken) {
      window.open("/", "_self");
    }
  }, [loggedOut]);

  useEffect(() => {
    if (!user && authToken) {
      dispatch(tryConfirmLogin({ authToken }));
    }
  }, [authToken]);

  return (
    <>
      <RouteGuard conditions={conditions} altRoute={altRoute} />
      {page}
    </>
  );
};

//lazy pages
const LHODashboard = Loadable(
  lazy(() => import("../pages/dashboards/HODashboard")),
);
const LAdmins = Loadable(lazy(() => import("../pages/Admins/Admins")));
const LTutors = Loadable(lazy(() => import("../pages/Staff/Staff")));
const LCustomers = Loadable(lazy(() => import("../pages/Customers/Customers")));
const LCourses = Loadable(lazy(() => import("../pages/Courses/Courses")));
const LOrders = Loadable(lazy(() => import("../pages/Orders/Orders")));
const LPaymentConfirmation = Loadable(
  lazy(() => import("../pages/PaymentConfirmation/PaymentConfirmation")),
);

const LProfile = Loadable(
  lazy(() => import("../pages/CustomerProfile/CustomerProfile")),
);
const LResourceHub = Loadable(
  lazy(() => import("../pages/ResourceHub/ResourceHub")),
);
const LMainArticle = Loadable(
  lazy(() => import("../pages/MainArticle/MainArticle")),
);
const LCustomerLMS = Loadable(
  lazy(() => import("../pages/CustomerLMS/CustomerLMS")),
);
const LCustomerLMSCourse = Loadable(
  lazy(() => import("../pages/CustomerLMSCourse/CustomerLMSCourse")),
);
const LCustomerHelp = Loadable(
  lazy(() => import("../pages/CustomerHelp/CustomerHelp")),
);
const LCustomerConsultancy = Loadable(
  lazy(() => import("../pages/CustomerConsultancy/CustomerConsultancy")),
);
const LCustomerInvestmentOpportunity = Loadable(
  lazy(
    () =>
      import("../pages/CustomerInvestmentOpportunity/CustomerInvestmentOpportunity"),
  ),
);

const LAboutEvent = Loadable(
  lazy(() => import("../pages/AboutEvent/AboutEvent")),
);
const LCustomerFavourites = Loadable(
  lazy(() => import("../pages/CustomerFavourites/CustomerFavourites")),
);
const LCustomerEvents = Loadable(
  lazy(() => import("../pages/CustomerEvents/CustomerEvents")),
);

const LCustomerProducts = Loadable(
  lazy(() => import("../pages/CustomerProducts/CustomerProducts")),
);

const CustomerRoutes = {
  path: "/",
  element: (
    <PageTemplate
      pageName=""
      altRoute={"/dashboard"}
      page={<GeneralLayout />}
    />
  ),
  children: [
    {
      path: "member/profile",
      element: (
        <PageTemplate
          pageName="member/profile"
          altRoute={"/profile"}
          page={<LProfile />}
        />
      ),
    },
    {
      path: "products",
      element: (
        <PageTemplate
          pageName="products"
          altRoute={"/sign-up"}
          page={<LCustomerProducts />}
        />
      ),
    },
    {
      path: "member/favorites",
      element: (
        <PageTemplate
          pageName="member/profile"
          altRoute={"/dashboard"}
          page={<LCustomerFavourites />}
        />
      ),
    },
    {
      path: "member/events",
      element: (
        <PageTemplate
          pageName="member/events"
          altRoute={"/dashboard"}
          page={<LCustomerEvents />}
        />
      ),
    },
    {
      path: "member/lms",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LCustomerLMS />}
        />
      ),
    },
    {
      path: "member/lms/course",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LCustomerLMSCourse />}
        />
      ),
    },
    {
      path: "member/resource-hub/article",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LMainArticle />}
        />
      ),
    },
    {
      path: "member/help",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LCustomerHelp />}
        />
      ),
    },
    {
      path: "services/other-services/consultancy",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LCustomerConsultancy />}
        />
      ),
    },
    {
      path: "services/other-services/investment-opportunity",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LCustomerInvestmentOpportunity />}
        />
      ),
    },
    {
      path: "welcome/events/about-event",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LAboutEvent />}
        />
      ),
    },
    {
      path: "member",
      element: (
        <PageTemplate
          pageName="member"
          altRoute={"/dashboard"}
          page={<LHODashboard />}
        />
      ),
    },
    {
      path: "orders",
      element: (
        <PageTemplate
          pageName="order-history"
          altRoute={"/dashboard"}
          page={<LOrders />}
        />
      ),
    },
    {
      path: "confirmations/payments/:paymentId",
      element: (
        <PageTemplate
          pageName="payment-confirmation"
          altRoute={"/dashboard"}
          page={<LPaymentConfirmation />}
        />
      ),
    },

    // {
    //     path: "dashboard/tutors",
    //     element: <PageTemplate pageName="dashboard/tutors" altRoute={'/dashboard'} page={<LTutors />} />
    // },
    // {
    //     path: "dashboard/members",
    //     element: <PageTemplate pageName="dashboard/members" altRoute={'/dashboard'} page={<LCustomers />} />
    // },
    // {
    //     path: "dashboard/courses",
    //     element: <PageTemplate pageName="dashboard/courses" altRoute={'/dashboard'} page={<LCourses />} />
    // },
  ],
};

export const CustomerAltRoutes = {
  path: "/",
  element: (
    <PageTemplate
      pageName=""
      altRoute={"/dashboard"}
      page={<GeneralLayoutAlt />}
    />
  ),
  children: [],
};

export default CustomerRoutes;
